import { css } from '@compiled/react';
import React, { useCallback } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import { CourseList, CourseStatus, formatMinutes } from '@atlassian/learning-components';
import { token } from '@atlaskit/tokens';

import { ContentfulLearningPath } from 'src/graphql/queries/GetLearningPath';
import { getCourseUrl, withPrefix } from 'src/utils/getUrl';
import { getLearningPathsHubPart } from 'src/utils/pathUtils';
import { getSlug } from 'src/utils/slug';
import { LearningPathLoaderProps } from 'src/pages/OldLearningPathPage';
import { shortenDurationString } from 'src/pages/LearningPathPage/utils';
import { getDuration } from 'src/pages/OldLearningPathPage/children/utils/learningPathUtils';

export const CourseListWrapper: React.FC = () => {
  const { learningPath } = useLoaderData() as LearningPathLoaderProps;
  const navigate = useNavigate();
  const location = useLocation();

  const handleHrefLink = useCallback(
    (course: ContentfulLearningPath['coursesCollection']['items'][0]): string => {
      if (!course || !learningPath) {
        return '';
      }

      const baseUrl = getCourseUrl(getSlug(course), learningPath?.slug);
      const hubPart = getLearningPathsHubPart(location.pathname);
      const url = hubPart ? `${hubPart}${baseUrl}` : baseUrl;

      return withPrefix(url);
    },
    [learningPath, location.pathname],
  );

  const learningPathDuration = shortenDurationString(
    formatMinutes(learningPath?.coursesCollection.items.reduce((acc, course) => acc + getDuration(course), 0)),
  );

  return (
    <div css={courseListContainerStyles} data-testid="course-list-wrapper">
      {learningPath.coursesCollection.items.map((course) => {
        const courseDuration = shortenDurationString(
          formatMinutes(course.lessonsCollection.items.reduce((total, lesson) => total + (lesson.duration || 0), 0)),
        );

        const courseData = {
          slug: course.slug,
          title: course.title,
          subTitle: course.teaser || '',
          duration: courseDuration,
          href: handleHrefLink(course),
          imageUrl: course?.reward?.badgeCdnUrl || course?.reward?.badge?.[0]?.cdn_url,
          courseStatus: CourseStatus.DEFAULT,
        };

        const lessons = course.lessonsCollection.items.map((lesson) => ({
          title: lesson.title,
          slug: lesson.slug,
          duration: learningPathDuration,
          isCompleted: false,
        }));

        return (
          <CourseList
            key={course.sys.id}
            course={courseData}
            lessons={lessons}
            data-testid={`course-list-${course.slug}`}
            onLessonClick={(slug) => {
              navigate(`/path/${learningPath.slug}/course/${course.slug}/lesson/${slug}`);
            }}
            onCourseClick={() => {
              navigate(`/path/${learningPath.slug}/course/${course.slug}`);
            }}
            data-testid-course-button="course-button"
            data-testid-lesson-button={`lesson-button-${course.slug}`}
          />
        );
      })}
    </div>
  );
};

const courseListContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
  alignSelf: 'stretch',
});
